/**
 * The per-field renderers of a form-flow `fields` block.
 *
 * Split from `FmFormFlowContent.tsx` so neither file crosses the 500-line gate.
 * `FmFormFlowContent` keeps block composition; one control's own rendering lives
 * here.
 */
import dayjs from 'dayjs';
import React from 'react';
import { FmChip } from './FmChip';
import { FmInline, FmText } from './FmComposition';
import { FmDateField } from './FmDateField';
import { FmDropzone } from './FmDropzone';
import { FmProgress } from './FmFeedback';
import { FmField } from './FmField';
import { FmSelect } from './FmSelect';
import { FmSelector } from './FmSelector';
import { FmTextField } from './FmTextField';
import { FmNumberField } from './FmNumberField';
import type {
  FmFormFlowEvent,
  FmFormFlowFieldDescriptor,
  FmFormFlowPageDescriptor,
} from './FmFormFlowPage.types';
import LanguageContext from '@/i18n/LanguageContext';
import { localDatePattern } from '@/utils/localIsoDate';
import { languageToBcp47 } from '@/utils/plannerFormatters';

const FILE_TONE = {
  queued: 'mute',
  uploading: 'info',
  ready: 'success',
  warning: 'warn',
  error: 'warn',
} as const;

function emit(descriptor: FmFormFlowPageDescriptor, event: FmFormFlowEvent): void {
  descriptor.onEvent?.(event);
}

export function Field({
  blockId,
  field,
  descriptor,
}: {
  blockId: string;
  field: FmFormFlowFieldDescriptor;
  descriptor: FmFormFlowPageDescriptor;
}): React.ReactElement {
  if (field.kind === 'files')
    return <FilesField blockId={blockId} field={field} descriptor={descriptor} />;
  const error = field.validation?.status === 'invalid' ? field.validation.message : undefined;
  const common = {
    label: field.label,
    htmlFor: `${blockId}-${field.id}`,
    'data-testid': `form-flow-field-${field.id}`,
  };
  if (field.kind === 'text')
    return (
      <TextField
        blockId={blockId}
        field={field}
        descriptor={descriptor}
        error={error}
        common={common}
      />
    );
  if (field.kind === 'number')
    return (
      <NumberField
        blockId={blockId}
        field={field}
        descriptor={descriptor}
        error={error}
        common={common}
      />
    );
  if (field.kind === 'date')
    return (
      <DateField
        blockId={blockId}
        field={field}
        descriptor={descriptor}
        error={error}
        common={common}
      />
    );
  if (field.kind === 'select')
    return (
      <SelectField
        blockId={blockId}
        field={field}
        descriptor={descriptor}
        error={error}
        common={common}
      />
    );
  return (
    <FmField {...common}>
      <FmSelector
        variant={field.presentation ?? 'segmented'}
        label={field.label}
        value={field.value}
        items={field.options.map((option) => ({
          label: option.label,
          value: option.value,
          disabled: option.disabled,
        }))}
        onChange={(value) =>
          emit(descriptor, { kind: 'field-change', blockId, fieldId: field.id, value })
        }
      />
      {error ? (
        <FmText size="caption" tone="negative" role="alert">
          {error}
        </FmText>
      ) : null}
    </FmField>
  );
}

type FieldOf<Kind extends FmFormFlowFieldDescriptor['kind']> = Extract<
  FmFormFlowFieldDescriptor,
  { kind: Kind }
>;
type FieldCommon = {
  label: string;
  htmlFor: string;
  'data-testid': string;
};
type FieldProps<Kind extends FmFormFlowFieldDescriptor['kind']> = {
  blockId: string;
  field: FieldOf<Kind>;
  descriptor: FmFormFlowPageDescriptor;
  error?: string;
  common: FieldCommon;
};

function FieldMessages({ helper, error }: { helper?: string; error?: string }): React.ReactElement {
  return (
    <>
      {helper ? (
        <FmText size="caption" tone="muted">
          {helper}
        </FmText>
      ) : null}
      {error ? (
        <FmText size="caption" tone="negative" role="alert">
          {error}
        </FmText>
      ) : null}
    </>
  );
}

function FilesField({
  blockId,
  field,
  descriptor,
}: Omit<FieldProps<'files'>, 'error' | 'common'>): React.ReactElement {
  return (
    <div className="fm-form-flow__field-wide">
      <FmDropzone
        title={field.prompt}
        hint={field.hint}
        accept={field.accept}
        multiple={field.multiple}
        ariaLabel={field.inputLabel}
        inputTestId={`${field.id}-input`}
        onFiles={(files) => emit(descriptor, { kind: 'files-selected', blockId, files })}
        rows={field.files.map((file) => ({
          id: file.id,
          name: (
            <FmInline gap="xs" wrap={false}>
              <FmText as="span">{file.name}</FmText>
              {file.sizeLabel ? (
                <FmText as="span" size="caption" tone="muted">
                  {file.sizeLabel}
                </FmText>
              ) : null}
            </FmInline>
          ),
          status:
            file.status === 'uploading' && file.progress !== undefined ? (
              <FmProgress percent={file.progress} size="sm" />
            ) : (
              <FmChip variant={FILE_TONE[file.status]}>{file.statusLabel}</FmChip>
            ),
          reason: file.error,
          rejected: file.status === 'error',
        }))}
      />
      {field.details ? (
        <details className="fm-form-flow__file-details">
          <summary>{field.details.label}</summary>
          <FmText size="caption" tone="muted">
            {field.details.body}
          </FmText>
        </details>
      ) : null}
    </div>
  );
}

function TextField({
  blockId,
  field,
  descriptor,
  error,
  common,
}: FieldProps<'text'>): React.ReactElement {
  return (
    <FmField {...common}>
      <FmTextField
        id={`${blockId}-${field.id}`}
        ariaLabel={field.label}
        value={field.value}
        placeholder={field.placeholder}
        multiline={field.multiline}
        rows={field.rows}
        required={field.required}
        error={error}
        onChange={(event) =>
          emit(descriptor, {
            kind: 'field-change',
            blockId,
            fieldId: field.id,
            value: event.target.value,
          })
        }
      />
      <FieldMessages helper={field.helper} error={error} />
    </FmField>
  );
}

function NumberField({
  blockId,
  field,
  descriptor,
  error,
  common,
}: FieldProps<'number'>): React.ReactElement {
  return (
    <FmField {...common}>
      <FmNumberField
        id={`${blockId}-${field.id}`}
        value={field.value}
        placeholder={field.placeholder}
        required={field.required}
        min={field.min}
        max={field.max}
        step={field.step}
        precision={field.precision}
        full
        error={error}
        onChange={(value) =>
          emit(descriptor, { kind: 'field-change', blockId, fieldId: field.id, value })
        }
        data-testid={`form-flow-field-${field.id}`}
      />
      <FieldMessages helper={field.helper} error={error} />
    </FmField>
  );
}

function DateField({
  blockId,
  field,
  descriptor,
  error,
  common,
}: FieldProps<'date'>): React.ReactElement {
  // The value stays ISO; the owner reads and types the date as the page prints it.
  const language = React.useContext(LanguageContext)?.language ?? 'fi';
  return (
    <FmField {...common}>
      <FmDateField
        id={`${blockId}-${field.id}`}
        ariaLabel={field.label}
        format={localDatePattern(languageToBcp47(language))}
        value={field.value ? dayjs(field.value) : null}
        placeholder={field.placeholder ?? field.label}
        onChange={(value) =>
          emit(descriptor, {
            kind: 'field-change',
            blockId,
            fieldId: field.id,
            value: value?.format('YYYY-MM-DD') ?? null,
          })
        }
      />
      <FieldMessages helper={field.helper} error={error} />
    </FmField>
  );
}

function SelectField({
  blockId,
  field,
  descriptor,
  error,
  common,
}: FieldProps<'select'>): React.ReactElement {
  return (
    <FmField {...common}>
      <FmSelect<string | number | null>
        id={`${blockId}-${field.id}`}
        ariaLabel={field.label}
        value={field.value}
        placeholder={field.placeholder}
        loading={field.loading}
        options={field.options.map((option) => ({
          label: option.label,
          value: option.value,
          disabled: option.disabled,
        }))}
        onChange={(value) =>
          emit(descriptor, { kind: 'field-change', blockId, fieldId: field.id, value })
        }
      />
      <FieldMessages error={error} />
    </FmField>
  );
}
